import { Api } from '@monorepo/shared/apiClient';
import {
  LogCacheEvents,
  logCacheRegister,
} from '@monorepo/shared/cacheRegisters/data2/logCacheRegister';
import {
  ResourceTypeCacheEvents,
  resourceTypeCacheRegister,
} from '@monorepo/shared/cacheRegisters/data2/resourceTypeCacheRegister';
import {
  WorkflowCacheEvents,
  workflowCacheRegister,
} from '@monorepo/shared/cacheRegisters/data2/workflowCacheRegister';
import {
  getKeyParamsForInvalidation,
  getQueryConfig,
} from '@monorepo/shared/utils/queryUtils';
import { queryCache, QueryConfig, useQuery } from 'react-query';

type KeyParams = Api.FetchDatasetByOriginIdParams;

const KEY_START = 'datasetByOrigin' as const;
export const createKey = ({ organizationId, originId }: KeyParams) =>
  [KEY_START, organizationId, originId] as const;

type Fetcher = Api.DataHookQueryFn<
  typeof createKey,
  typeof Api.fetchDatasetByOriginId
>;

const fetcher: Fetcher = (_, organizationId, originId) =>
  Api.fetchDatasetByOriginId({ organizationId, originId });

type UseDatasetForOriginParams = Partial<Api.FetchDatasetByOriginIdParams> & {
  config?: QueryConfig<Api.FetchDatasetByOriginIdResponse, Api.ErrorResponse>;
};

export function useDatasetForOrigin({
  config: inputConfig,
  organizationId,
  originId,
}: UseDatasetForOriginParams) {
  const isEnabled = !!organizationId && !!originId;
  const config = getQueryConfig(inputConfig, isEnabled);
  const key = isEnabled ? createKey({ organizationId, originId }) : undefined;

  const { data, ...queryInfo } = useQuery(key, fetcher, config);
  return { ...queryInfo, dataset: data };
}

type QueryCache = ReturnType<typeof useDatasetForOrigin>['dataset'];

export function getCache(keyParams: KeyParams): QueryCache {
  return queryCache.getQueryData<QueryCache>(createKey(keyParams));
}

type InvalidateCacheParams = Pick<KeyParams, 'organizationId'>;

export async function invalidateCache(
  keyParams: InvalidateCacheParams,
): Promise<void> {
  await queryCache.invalidateQueries(
    getKeyParamsForInvalidation([KEY_START, keyParams.organizationId]),
  );
}

const cacheRegisterInvalidator = {
  hookName: 'useDatasetForOriginId',
  callback: (keyParams: InvalidateCacheParams) => invalidateCache(keyParams),
};

resourceTypeCacheRegister(
  [ResourceTypeCacheEvents.UPDATE],
  cacheRegisterInvalidator,
);

logCacheRegister([LogCacheEvents.UPDATE], cacheRegisterInvalidator);

workflowCacheRegister([WorkflowCacheEvents.UPDATE], cacheRegisterInvalidator);
